import React, { useState, useEffect } from 'react'
import { apiGet } from '../../utils/api'

interface Rating {
  id: string
  chat_id: string
  customer_id: string
  rating: number
  feedback_text?: string
  agent_id?: string
  created_at: string
}

export const RatingsView: React.FC = () => {
  const [ratings, setRatings] = useState<Rating[]>([])
  const [average, setAverage] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadRatings()
  }, [])

  const loadRatings = async () => {
    try {
      const result = await apiGet<{ ratings: Rating[]; average: number; total: number }>('/ratings')
      setRatings(result.ratings)
      setAverage(result.average)
      setTotal(result.total)
    } catch (err) {
      console.error('Failed to load ratings:', err)
    } finally {
      setLoading(false)
    }
  }

  const ratingDistribution = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: ratings.filter(r => r.rating === star).length,
    pct: ratings.length > 0 ? (ratings.filter(r => r.rating === star).length / ratings.length) * 100 : 0,
  }))

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 pt-6 pb-4 bg-surface border-b border-border">
        <h1 className="text-xl font-bold text-txt-primary mb-1">Customer Ratings</h1>
        <p className="text-sm text-txt-secondary">Track customer satisfaction across all conversations</p>
      </div>

      {/* Summary cards */}
      <div className="px-6 py-4 border-b border-border">
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-surface-secondary border border-border">
            <p className="text-xs font-semibold text-txt-tertiary uppercase tracking-wider mb-1">Average Rating</p>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-txt-primary">{average.toFixed(1)}</span>
              <div className="flex">
                {[1, 2, 3, 4, 5].map(star => (
                  <svg key={star} className={`w-4 h-4 ${star <= Math.round(average) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                  </svg>
                ))}
              </div>
            </div>
          </div>
          <div className="p-4 rounded-lg bg-surface-secondary border border-border">
            <p className="text-xs font-semibold text-txt-tertiary uppercase tracking-wider mb-1">Total Ratings</p>
            <span className="text-2xl font-bold text-txt-primary">{total}</span>
          </div>
          <div className="p-4 rounded-lg bg-surface-secondary border border-border">
            <p className="text-xs font-semibold text-txt-tertiary uppercase tracking-wider mb-1">Distribution</p>
            <div className="space-y-1 mt-1">
              {ratingDistribution.map(d => (
                <div key={d.star} className="flex items-center gap-2">
                  <span className="text-[10px] w-3 text-txt-tertiary">{d.star}</span>
                  <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400 rounded-full" style={{ width: `${d.pct}%` }} />
                  </div>
                  <span className="text-[10px] text-txt-tertiary w-6 text-right">{d.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Ratings list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48"><span className="text-sm text-txt-secondary">Loading ratings...</span></div>
        ) : ratings.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48">
            <p className="text-sm text-txt-secondary">No ratings yet</p>
          </div>
        ) : (
          ratings.map(r => (
            <div key={r.id} className="px-6 py-3 border-b border-border hover:bg-surface-hover/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map(star => (
                      <svg key={star} className={`w-4 h-4 ${star <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                        <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                      </svg>
                    ))}
                  </div>
                  <span className="text-sm text-txt-primary">Chat #{r.chat_id.substring(0, 8)}</span>
                  {r.feedback_text && (
                    <span className="text-xs text-txt-tertiary italic">"{r.feedback_text}"</span>
                  )}
                </div>
                <span className="text-xs text-txt-tertiary">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
